import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Gauge,
  Search,
  MapPin,
  Sparkles,
  Swords,
  MousePointer2,
  Clock,
  ArrowRight,
  Loader2,
} from "lucide-react";

const CHECKLIST = [
  { icon: Gauge, label: "Website Speed" },
  { icon: Search, label: "SEO Audit" },
  { icon: MapPin, label: "Google Business Profile" },
  { icon: Sparkles, label: "AI Visibility" },
  { icon: Swords, label: "Competitor Analysis" },
  { icon: MousePointer2, label: "UX Review" },
];

export default function AuditForm() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    website: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.website) {
      toast.error("Please fill in your name, email and website.");
      return;
    }
    setLoading(true);
    // Mocked submission
    await new Promise((r) => setTimeout(r, 1100));
    setLoading(false);
    toast.success("Audit request received — we'll be in touch within 24 hours.", {
      description: `Thanks ${form.name.split(" ")[0]}, our team is reviewing ${form.website} now.`,
    });
    setForm({ name: "", email: "", website: "", message: "" });
  };

  return (
    <section
      id="audit"
      data-testid="audit-section"
      className="section relative"
    >
      <div className="absolute inset-0 radial-glow opacity-50 pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-12 items-start">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
        >
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-300 text-xs font-semibold tracking-widest uppercase">
            Free Website Audit
          </div>
          <h2 className="mt-5 font-display text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]">
            Get your complimentary{" "}
            <span className="text-gradient-brand">website audit</span>.
          </h2>
          <p className="mt-5 text-white/60 text-lg max-w-md">
            A senior strategist will personally review your site and send a
            no-fluff growth report covering:
          </p>

          <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-md">
            {CHECKLIST.map((c, i) => (
              <li
                key={c.label}
                data-testid={`audit-checklist-${i}`}
                className="flex items-center gap-3 px-4 py-3 rounded-xl border border-white/10 bg-white/[0.025]"
              >
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-600/20 to-cyan-400/10 border border-white/10 flex items-center justify-center">
                  <c.icon className="w-4 h-4 text-cyan-400" />
                </div>
                <span className="text-sm font-medium">{c.label}</span>
              </li>
            ))}
          </ul>

          <div className="mt-8 inline-flex items-center gap-2 text-sm text-white/70">
            <Clock className="w-4 h-4 text-cyan-400" />
            <span>
              Estimated delivery:{" "}
              <span className="text-white font-semibold">Within 24 hours</span>
            </span>
          </div>
        </motion.div>

        <motion.form
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6, delay: 0.1 }}
          onSubmit={submit}
          data-testid="audit-form"
          className="relative rounded-3xl p-[1px] bg-gradient-to-br from-blue-600/40 via-cyan-400/30 to-transparent"
        >
          <div className="rounded-3xl bg-[#0a0a0a] p-8 md:p-10">
            <div className="space-y-5">
              <Field
                label="Full Name"
                id="name"
                placeholder="Jane Carter"
                value={form.name}
                onChange={update("name")}
              />
              <Field
                label="Email"
                id="email"
                type="email"
                placeholder="jane@company.com"
                value={form.email}
                onChange={update("email")}
              />
              <Field
                label="Website URL"
                id="website"
                placeholder="https://yourbusiness.com"
                value={form.website}
                onChange={update("website")}
              />
              <div>
                <label
                  htmlFor="message"
                  className="text-xs font-semibold uppercase tracking-widest text-white/50"
                >
                  Message <span className="text-white/30">(optional)</span>
                </label>
                <textarea
                  id="message"
                  data-testid="audit-input-message"
                  value={form.message}
                  onChange={update("message")}
                  placeholder="Tell us about your goals…"
                  rows={4}
                  className="mt-2 w-full bg-white/[0.04] border border-white/10 focus:border-cyan-500/40 focus:outline-none rounded-xl px-4 py-3 text-white placeholder:text-white/30 transition-colors resize-none"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                data-testid="audit-submit"
                className="w-full inline-flex items-center justify-center gap-2 bg-gradient-to-r from-blue-600 to-cyan-400 text-black font-semibold py-4 rounded-full hover:shadow-[0_0_40px_rgba(0,229,255,0.4)] transition-all duration-300 disabled:opacity-60"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" /> Sending…
                  </>
                ) : (
                  <>
                    Get My Free Audit{" "}
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>

              <p className="text-xs text-white/40 text-center">
                We respect your inbox. No spam, ever.
              </p>
            </div>
          </div>
        </motion.form>
      </div>
    </section>
  );
}

function Field({ label, id, ...rest }) {
  return (
    <div>
      <label
        htmlFor={id}
        className="text-xs font-semibold uppercase tracking-widest text-white/50"
      >
        {label}
      </label>
      <input
        id={id}
        data-testid={`audit-input-${id}`}
        className="mt-2 w-full bg-white/[0.04] border border-white/10 focus:border-cyan-500/40 focus:outline-none rounded-xl px-4 py-3 text-white placeholder:text-white/30 transition-colors"
        {...rest}
      />
    </div>
  );
}
